import BaseApi from '../../api/BaseApi.js';
import { unwrap } from '../calendar-month/utils/apiEnvelope.js';
import { serverData } from './serverBootstrap.js';

/**
 * credentialsApi — Symfony client for the Credentials page.
 *
 * Auth is the shared one: `BaseApi` sends the PHPSESSID cookie as the `token`
 * header (`api/utils.js`). No Bearer, no separate login.
 *
 * SCOPE. Every resource exists twice in Symfony: `/api/v1/user/…` for the
 * signed-in user and `/api/v1/org/{org}/user/{id}/…` for an administrator
 * looking at someone else. `scope()` picks between them by comparing the
 * profile on screen (`serverData().user.id`, or the id in the URL) with the
 * session's own id (`serverData().userID`) — the page is reachable both ways
 * and sending an admin to the self-routes would silently show them their own
 * record.
 *
 * Every method returns the payload already unwrapped: Symfony answers
 * `{status, data}` and `apiRequest` wraps that again, so a caller would
 * otherwise write `res.data.data` everywhere.
 */
/**
 * What to tell the user about a failed call: the API's own words (its
 * envelope's `error` — a string or a list — or `message`), not the transport's
 * "HTTP 422: Unprocessable Content".
 */
export function apiErrorText(e) {
	const body = e && e.response && e.response.data;
	const err = body && body.error;
	if (Array.isArray(err) && err.length) return err.join(' ');
	if (typeof err === 'string' && err.trim()) return err;
	if (body && typeof body.message === 'string' && body.message.trim()) return body.message;
	return (e && e.message) || 'Something went wrong.';
}

class CredentialsApi extends BaseApi {
	/** The profile being viewed — not necessarily the signed-in user. */
	static viewedUserId() {
		const d = serverData();
		const fromPayload = d.user && d.user.id;
		const id = Number(fromPayload != null ? fromPayload : d._element);
		return Number.isFinite(id) && id > 0 ? id : 0;
	}

	static isSelf() {
		const sessionId = Number(serverData().userID || 0);
		const viewed = this.viewedUserId();
		// No viewed id (a route without an element) means "my own page".
		return viewed === 0 || sessionId === viewed;
	}

	/** `/user{path}` for yourself, `/org/{org}/user/{id}{path}` for anyone else. */
	static scope(path) {
		return this.isSelf()
			? `/user${path}`
			: this.buildOrgEndpoint(`/user/${this.viewedUserId()}${path}`);
	}

	static async read(endpoint) {
		return unwrap(await this.get(endpoint));
	}

	// ─── Profile ─────────────────────────────────────────────────────────────

	/** Personal Information (`UpdateUserProfileRequestDto`); own or, for a manager, a member's. */
	static async updateProfile(body) {
		return unwrap(await this.put(this.scope('/profile'), body));
	}

	/**
	 * `{countryId: [{id, name}]}` — `GET /countries` lists only the countries
	 * that have states, each with them; a country missing here has none.
	 */
	static async getStatesByCountry() {
		const list = unwrap(await this.get('/countries'));
		const byCountry = {};
		(Array.isArray(list) ? list : []).forEach(c => {
			if (!c || c.id == null) return;
			byCountry[String(c.id)] = (Array.isArray(c.states) ? c.states : [])
				.filter(st => st && st.id != null)
				.map(st => ({ id: String(st.id), name: st.name || '' }))
				.sort((x, y) => x.name.localeCompare(y.name));
		});
		return byCountry;
	}

	// ─── Avatar (public Spaces bucket; URLs come back absolute) ───────────────

	/** `{id, name, smallImage, mediumImage, bigImage, originalImage}`, or null when none (404). */
	static async getAvatar(userId) {
		try {
			return unwrap(await this.get(`/user/${userId}/avatar`));
		} catch (e) {
			if (e?.response?.status === 404) return null;
			throw e;
		}
	}

	/**
	 * Writes go to `/user/{id}/avatar` for your own photo only; a member's photo
	 * goes through the org route, which checks the admin's rights.
	 */
	static avatarEndpoint(userId) {
		return this.isSelf()
			? `/user/${userId}/avatar`
			: this.buildOrgEndpoint(`/user/${userId}/avatar`);
	}

	/** Own avatar, or a member's for an org admin. */
	static async uploadAvatar(userId, file) {
		const form = new FormData();
		form.append('file', file);
		return unwrap(
			await this.apiRequest(this.avatarEndpoint(userId), { method: 'POST', body: form })
		);
	}

	static async deleteAvatar(userId) {
		return unwrap(await this.delete(this.avatarEndpoint(userId)));
	}

	// ─── Organizations ───────────────────────────────────────────────────────

	/** The signed-in user's organizations: `[{id, name, unique, image, status, roles[]}]`. */
	static async getMyOrganizations() {
		const data = unwrap(await this.get('/user/organizations'));
		return (data && data.organizations) || [];
	}

	/**
	 * A member's roles in the CURRENT organization only (an admin never sees
	 * the member's other organizations): their row from `/users-list`.
	 */
	static async getMemberInOrganization(userId) {
		const data = unwrap(await this.get(this.buildOrgEndpoint('/users-list')));
		const rows = Array.isArray(data) ? data : (data && (data.users || data.items)) || [];
		return rows.find(u => String(u.id) === String(userId)) || null;
	}

	// ─── Catalogs (not user-scoped) ──────────────────────────────────────────

	/** `{types, grades, ratings, ratingCategories, fieldVisibility, fieldLabels}` */
	static getCertificateMetadata() {
		return this.read('/certificate-metadata');
	}

	/** Document types visible to the caller: system types + their orgs'. */
	static getDocumentTypes() {
		return this.read('/user/document-types');
	}

	// ─── Documents ───────────────────────────────────────────────────────────

	static getDocuments() {
		return this.read(this.scope('/document'));
	}

	static async createDocument(body) {
		return unwrap(await this.post(this.scope('/document'), body));
	}

	static async updateDocument(documentId, body) {
		return unwrap(await this.put(this.scope(`/document/${documentId}`), body));
	}

	static async removeDocument(documentId) {
		return unwrap(await this.delete(this.scope(`/document/${documentId}`)));
	}

	/**
	 * Attach a file to a document. Images and other files go to different
	 * endpoints, as they do in storage — the controller reads the part named
	 * `file` in both cases.
	 */
	static async uploadDocumentAsset(documentId, file, kind) {
		const form = new FormData();
		form.append('file', file);
		return unwrap(
			await this.apiRequest(this.scope(`/document/${documentId}/${kind}`), {
				method: 'POST',
				body: form
			})
		);
	}

	static async removeDocumentAsset(documentId, kind, assetId) {
		return unwrap(await this.delete(this.scope(`/document/${documentId}/${kind}/${assetId}`)));
	}

	// ─── Certificates ────────────────────────────────────────────────────────

	static getCertificates() {
		return this.read(this.scope('/certificate'));
	}

	/** The server derives medical expiries and reminders; client-sent ones are ignored. */
	static async createCertificate(body) {
		return unwrap(await this.post(this.scope('/certificate'), body));
	}

	/** Partial: a null field is left as stored. */
	static async updateCertificate(certificateId, body) {
		return unwrap(await this.put(this.scope(`/certificate/${certificateId}`), body));
	}

	static async removeCertificate(certificateId) {
		return unwrap(await this.delete(this.scope(`/certificate/${certificateId}`)));
	}

	/** `privilegeId` null / 0 = the rating carries no privilege (legacy "none"). */
	static async addCertificateRating(certificateId, ratingId, privilegeId = null) {
		return unwrap(
			await this.post(this.scope(`/certificate/${certificateId}/rating`), { ratingId, privilegeId })
		);
	}

	/** By the rating RELATION id (`relId`), not the rating id. */
	static async removeCertificateRating(certificateId, relId) {
		return unwrap(
			await this.delete(this.scope(`/certificate/${certificateId}/rating/${relId}`))
		);
	}

	/** Attach a scan to a certificate; `kind` is 'image' or 'file' (separate endpoints). */
	static async uploadCertificateAsset(certificateId, file, kind) {
		const form = new FormData();
		form.append('file', file);
		return unwrap(
			await this.apiRequest(this.scope(`/certificate/${certificateId}/${kind}`), {
				method: 'POST',
				body: form
			})
		);
	}

	static async removeCertificateAsset(certificateId, kind, assetId) {
		return unwrap(
			await this.delete(this.scope(`/certificate/${certificateId}/${kind}/${assetId}`))
		);
	}

	// ─── Endorsements ────────────────────────────────────────────────────────

	/** `{id, complex, performance, tailWheel, highAltitudeOperations}` */
	static getEndorsement() {
		return this.read(this.scope('/endorsement'));
	}

	/** Partial update — only the flags present are written. */
	static async updateEndorsement(flags) {
		return unwrap(await this.put(this.scope('/endorsement'), flags));
	}

	static async uploadEndorsementAsset(typeKey, file, kind) {
		const form = new FormData();
		form.append('file', file);
		return unwrap(
			await this.apiRequest(this.scope(`/endorsement/${typeKey}/${kind}`), {
				method: 'POST',
				body: form
			})
		);
	}

	static async removeEndorsementAsset(typeKey, kind) {
		return unwrap(await this.delete(this.scope(`/endorsement/${typeKey}/${kind}`)));
	}

	// ─── Flight reviews ──────────────────────────────────────────────────────

	/** A LIST — FAA and per-organization reviews together, each with its type. */
	static getFlightReviews() {
		return this.read(this.scope('/flight-review'));
	}

	/**
	 * Create the FAA review a user doesn't have yet (409 when it exists).
	 * `type: 'faa'` is explicit: the org route defaults to an ORGANIZATION review.
	 */
	static async createFlightReview(body) {
		return unwrap(await this.post(this.scope('/flight-review'), { ...body, type: 'faa' }));
	}

	static async updateFlightReview(reviewId, body) {
		return unwrap(await this.put(this.scope(`/flight-review/${reviewId}`), body));
	}

	static async uploadFlightReviewAsset(reviewId, file, kind) {
		const form = new FormData();
		form.append('file', file);
		return unwrap(
			await this.apiRequest(this.scope(`/flight-review/${reviewId}/${kind}`), {
				method: 'POST',
				body: form
			})
		);
	}

	static async removeFlightReviewAsset(reviewId, kind, assetId) {
		return unwrap(await this.delete(this.scope(`/flight-review/${reviewId}/${kind}/${assetId}`)));
	}
}

export default CredentialsApi;
